import { serializeContext } from '@/lib/utils/errorWithContext';
import type { ValidationIssueWithPath } from '@/lib/utils/validator/ValidationResult';

/**
 * The Error thrown by `validate` when an issue is reported about the parsed
 * value.
 */
export class ValidationError extends Error {
  readonly value: unknown;
  readonly issues: Issue[];

  constructor(message: string, value: unknown, issues: Issue[]) {
    super(`${message} ${JSON.stringify(issues)}`);
    this.value = value;
    this.issues = issues;
  }
}

export type Issue = ValidationIssueWithPath;

export function isValidationError(error: unknown): error is ValidationError {
  return error instanceof ValidationError;
}

/**
 * Serializes the `issues` & `value` attributes of a ValidationError into a context object.
 * For any object in the `value` attribute that has an `id` key, only keep the `id`.
 */
export function getValidationErrorContext(error: ValidationError): Record<string, string> {
  const { issues, value } = error;
  const strippedValue = stripValidationErrorValue(value);
  return serializeContext({
    issues,
    value: strippedValue,
  });
}

/**
 * Takes any value iterates over it.
 * When it encounters an object with an `id` key, it strips all other keys from the object
 * to clean up pii and reduce noise.
 * Returns all other values.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value != null && !Array.isArray(value);
}

function hasId(value: unknown): value is { id: unknown } {
  return isRecord(value) && 'id' in value;
}

function stripValidationErrorValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((e) => stripValidationErrorValue(e));
  }

  if (isRecord(value)) {
    if (hasId(value)) {
      return { id: value.id };
    }

    const result: Record<string, unknown> = {};
    for (const key in value) {
      if (Object.hasOwn(value, key)) {
        result[key] = stripValidationErrorValue(value[key]);
      }
    }
    return result;
  }

  return value;
}
